import { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, Video, Music, Upload, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function MediaUploadPage() {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const [uploading, setUploading] = useState(false);
    const [uploadType, setUploadType] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const photoInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const musicInputRef = useRef(null);

    const getToken = () => localStorage.getItem('token') || localStorage.getItem('auth_token');

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploading(true);
        setUploadType('photo');

        try {
            let uploaded = 0;
            for (const file of files) {
                if (file.size > 20 * 1024 * 1024) {
                    toast.error(`${file.name} ist zu groß (max. 20MB)`);
                    continue;
                }
                const formData = new FormData();
                formData.append('photo', file);
                formData.append('guest_name', 'Event Owner');

                const token = getToken();
                const headers = { 'Content-Type': 'multipart/form-data' };
                if (token) headers.Authorization = `Bearer ${token}`;

                await axios.post(`${API}/events/${eventId}/photos`, formData, { headers });
                uploaded++;
            }
            toast.success(`${uploaded} Foto(s) hochgeladen! 📸`);
            setUploadedFiles(prev => [...prev, ...files.map(f => ({ name: f.name, type: 'photo' }))]);
        } catch (error) {
            console.error('Photo upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            setUploadType(null);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 100 * 1024 * 1024) {
            toast.error('Video ist zu groß (max. 100MB)');
            return;
        }

        setUploading(true);
        setUploadType('video');

        try {
            const formData = new FormData();
            formData.append('video', file);

            const token = getToken();
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) headers.Authorization = `Bearer ${token}`;

            await axios.post(`${API}/events/${eventId}/videos`, formData, { headers });
            toast.success('Video hochgeladen! 🎬');
            setUploadedFiles(prev => [...prev, { name: file.name, type: 'video' }]);
        } catch (error) {
            console.error('Video upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            setUploadType(null);
            if (videoInputRef.current) videoInputRef.current.value = "";
        }
    };

    const handleMusicUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) {
            toast.error('Musikdatei ist zu groß (max. 50MB)');
            return;
        }

        if (!file.type.startsWith('audio/')) {
            toast.error('Bitte nur Audiodateien hochladen');
            return;
        }

        setUploading(true);
        setUploadType('music');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('added_by_name', 'Event Owner');

            const token = getToken();
            const headers = { 'Content-Type': 'multipart/form-data' };
            if (token) headers.Authorization = `Bearer ${token}`;

            await axios.post(`${API}/playlists/events/${eventId}/playlist/upload`, formData, { headers });
            toast.success('Song hochgeladen! 🎵');
            setUploadedFiles(prev => [...prev, { name: file.name, type: 'music' }]);
        } catch (error) {
            console.error('Music upload failed', error);
            toast.error('Upload fehlgeschlagen');
        } finally {
            setUploading(false);
            setUploadType(null);
            if (musicInputRef.current) musicInputRef.current.value = "";
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Medien hochladen</h1>
                        <p className="text-sm text-gray-500 mt-1">Lade Fotos, Videos oder Musik für dein Event hoch</p>
                    </div>
                </div>

                {/* Upload Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Photo Upload */}
                    <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center">
                                <Camera className="w-8 h-8 text-pink-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="font-bold text-gray-900 text-lg">Fotos</h3>
                                <p className="text-sm text-gray-500 mt-1">JPG, PNG (max. 20MB)</p>
                            </div>
                            <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handlePhotoUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => !uploading && photoInputRef.current?.click()}
                                disabled={uploading && uploadType === 'photo'}
                                className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-medium rounded-xl transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {uploading && uploadType === 'photo' ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Lädt...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Hochladen
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Video Upload */}
                    <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                                <Video className="w-8 h-8 text-blue-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="font-bold text-gray-900 text-lg">Videos</h3>
                                <p className="text-sm text-gray-500 mt-1">MP4, MOV (max. 100MB)</p>
                            </div>
                            <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/*"
                                onChange={handleVideoUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => !uploading && videoInputRef.current?.click()}
                                disabled={uploading && uploadType === 'video'}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {uploading && uploadType === 'video' ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Lädt...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Hochladen
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Music Upload */}
                    <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center">
                                <Music className="w-8 h-8 text-purple-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="font-bold text-gray-900 text-lg">Musik</h3>
                                <p className="text-sm text-gray-500 mt-1">MP3, WAV (max. 50MB)</p>
                            </div>
                            <input
                                ref={musicInputRef}
                                type="file"
                                accept="audio/*"
                                onChange={handleMusicUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => !uploading && musicInputRef.current?.click()}
                                disabled={uploading && uploadType === 'music'}
                                className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {uploading && uploadType === 'music' ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Lädt...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Hochladen
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Upload History */}
                {uploadedFiles.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 shadow-md">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-600" />
                            Hochgeladen ({uploadedFiles.length})
                        </h3>
                        <div className="space-y-2">
                            {uploadedFiles.map((file, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${file.type === 'photo' ? 'bg-pink-100' :
                                            file.type === 'video' ? 'bg-blue-100' : 'bg-purple-100'
                                        }`}>
                                        {file.type === 'photo' && <Camera className="w-5 h-5 text-pink-600" />}
                                        {file.type === 'video' && <Video className="w-5 h-5 text-blue-600" />}
                                        {file.type === 'music' && <Music className="w-5 h-5 text-purple-600" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">{file.type}</p>
                                    </div>
                                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
